/**
 * ItemReservationCard - Individual item reservation display card
 */

import React from 'react';
import {
  EyeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../../contexts/AuthContext';
import { useRentalStatus } from '../hooks/useRentalStatus';
import { getBorderColorClass, getStatusDescription } from '../utils/rentalStatusUtils';

const ItemReservationCard = ({
  reservation,
  onViewDetails,
  onProcessReturn,
  onCancel,
  onReprocess,
  onDelete,
  onEdit,
  canProcessReturns,
  taxRate
}) => {
  const { hasPermission } = useAuth();
  const { rentalStatus, settings } = useRentalStatus(reservation);

  // Format date and time for display - properly convert UTC to local time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return { date: 'N/A', time: 'N/A' };

    try {
      // Ensure we're parsing as UTC and converting to local time
      const date = new Date(dateTime + (dateTime.includes('Z') ? '' : 'Z'));
      const dateOptions = { month: 'short', day: 'numeric' };
      const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };

      return {
        date: date.toLocaleDateString('en-US', dateOptions),
        time: date.toLocaleTimeString('en-US', timeOptions)
      };
    } catch (error) {
      console.error('Error formatting date:', error, 'for dateTime:', dateTime);
      return { date: 'Invalid', time: 'Date' };
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return '🟢';
      case 'COMPLETED':
        return '✅';
      case 'CANCELLED':
        return '❌';
      case 'PENDING':
        return '🟡';
      default:
        return '⚪';
    }
  };

  const calculateTotal = () => {
    const baseAmount = parseFloat(reservation.totalAmount || 0);
    const overageAmount = parseFloat(reservation.overageAmount || 0);
    const subtotal = baseAmount + overageAmount;
    const tax = subtotal * (taxRate / 100);
    return subtotal + tax;
  };

  const handleCancelClick = () => {
    if (window.confirm('Are you sure you want to cancel this reservation?')) {
      onCancel(reservation.id, { 
        status: 'CANCELLED',
        cancelledAt: new Date().toISOString()
      });
    }
  };

  const handleReprocessClick = () => {
    if (window.confirm('Are you sure you want to reprocess this reservation?')) {
      onReprocess(reservation.id);
    }
  };

  const handleDeleteClick = () => {
    onDelete(reservation.id);
  };

  // Get border color based on rental status
  const borderColorClass = getBorderColorClass(rentalStatus);
  const statusDescription = getStatusDescription(rentalStatus, reservation, settings.maxRentalDurationHours, settings.notificationFlagMinutes);

  return (
    <div className={`bg-white border rounded-lg p-6 hover:shadow-md transition-shadow ${borderColorClass}`}>
      {/* Rental Status Warning Banner */}
      {statusDescription && (
        <div className={`mb-4 p-3 rounded-md ${rentalStatus === 'OVERDUE' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-yellow-50 text-yellow-800 border border-yellow-200'}`}>
          <p className="text-sm font-medium">{statusDescription}</p>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        
        {/* Left Section - Basic Info */}
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {reservation.customer ? 
                  `${reservation.customer.firstName} ${reservation.customer.lastName}` : 
                  'Unknown Customer'
                }
              </h3>
              <p className="text-sm text-gray-600">
                {reservation.customer?.phone || 'No phone'}
              </p>
            </div>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(reservation.status)}`}>
              <span className="mr-1">{getStatusIcon(reservation.status)}</span>
              {reservation.status}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {/* Item Information */}
            <div>
              <div className="font-medium text-gray-700 mb-1">Item</div>
              <div className="text-gray-900">
                {reservation.inventoryItem ? (
                  <>
                    <div className="font-medium">
                      {reservation.inventoryItem.productType?.name || 'Unknown Type'} #{reservation.inventoryItem.itemNumber}
                    </div>
                    <div className="text-gray-600">
                      Condition: {reservation.inventoryItem.condition || 'Unknown'}
                    </div>
                  </>
                ) : (
                  <span className="text-red-600">Item data missing</span>
                )}
              </div>
            </div>

            {/* Time Information */}
            <div>
              <div className="font-medium text-gray-700 mb-1">Time</div>
              <div className="text-gray-900">
                <div>Start: {formatDateTime(reservation.startTime).date}, {formatDateTime(reservation.startTime).time}</div>
                <div>Duration: {reservation.reservationDuration || 'Unknown'} hours</div>
                {reservation.actualReturnTime && (
                  <div>Returned: {formatDateTime(reservation.actualReturnTime).date}, {formatDateTime(reservation.actualReturnTime).time}</div>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div>
              <div className="font-medium text-gray-700 mb-1">Payment</div>
              <div className="text-gray-900">
                <div>Base: {formatCurrency(reservation.totalAmount)}</div>
                {reservation.overageAmount > 0 && (
                  <div className="text-red-600">Overage: {formatCurrency(reservation.overageAmount)}</div>
                )}
                <div className="font-medium">Total: {formatCurrency(calculateTotal())}</div>
                <div className="text-xs text-gray-600">
                  Deposit: {formatCurrency(reservation.depositAmount)} ({reservation.depositType || 'Unknown'})
                </div>
              </div>
            </div>

            {/* Created Information */}
            <div>
              <div className="font-medium text-gray-700 mb-1">Created</div>
              <div className="text-gray-900">
                {(() => {
                  const createdDateTime = formatDateTime(reservation.createdAt);
                  return <div>{createdDateTime.date}, {createdDateTime.time}</div>;
                })()}
                <div className="text-xs text-gray-600">
                  By: {reservation.createdByEmployee ?
                    `${reservation.createdByEmployee.firstName} ${reservation.createdByEmployee.lastName}` :
                    'Unknown'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {reservation.notes && (
            <div className="pt-2 border-t border-gray-100">
              <div className="font-medium text-gray-700 mb-1 text-sm">Notes</div>
              <p className="text-sm text-gray-600">{reservation.notes}</p>
            </div>
          )}
        </div>

        {/* Right Section - Actions */}
        <div className="flex-shrink-0 flex flex-row lg:flex-col gap-2">
          {/* View Details Button */}
          {hasPermission('reservations', 'view') && (
            <button
              onClick={() => onViewDetails(reservation)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              Details
            </button>
          )}

          {/* Edit Button */}
          {reservation.status === 'ACTIVE' && hasPermission('reservations', 'create') && (
            <button
              onClick={() => onEdit(reservation)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PencilIcon className="h-4 w-4 mr-1" />
              Edit
            </button>
          )}

          {/* Process Return Button */}
          {canProcessReturns && reservation.status === 'ACTIVE' && (
            <button
              onClick={() => onProcessReturn(reservation)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              Return
            </button>
          )}

          {/* Cancel Button */}
          {reservation.status === 'ACTIVE' && hasPermission('reservations', 'cancel') && (
            <button
              onClick={handleCancelClick}
              className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <XCircleIcon className="h-4 w-4 mr-1" />
              Cancel
            </button>
          )}

          {/* Reprocess Button */}
          {canProcessReturns && reservation.status === 'COMPLETED' && (
            <button
              onClick={handleReprocessClick}
              className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Reprocess
            </button>
          )}

          {/* Delete Button */}
          {hasPermission('reservations', 'delete') && ['CANCELLED', 'COMPLETED'].includes(reservation.status) && (
            <button
              onClick={handleDeleteClick}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemReservationCard;